// src/pages/Public/BlogSection.tsx
import React, { useMemo, useState } from "react";
import { FaThLarge, FaList, FaCalendar, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { PublicEcrPost } from "../../types/ecr-post";
import { useFetchAllBlogs } from "../../hooks/all.hooks";
import ShareBar from "../../components/ShareBar";

const placeholderImg = "/assets/images/libsense.png";

// const stripHtml = (html?: string, max = 160) => {
//   if (!html) return "";
//   const text = html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
//   return text.length > max ? text.slice(0, max) + "…" : text;
// };

const BlogSection: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [page, setPage] = useState<number>(1);
  const limit = 6;

  const { data, isLoading, isError } = useFetchAllBlogs(
    selectedYear,
    page,
    limit,
    (error) => {
      console.error("Error fetching blogs:", error);
    }
  );

  const posts: PublicEcrPost[] = data?.posts || [];
  console.log("Fetched posts:", data?.posts);
  const years: number[] = data?.years || [currentYear];
  const totalPages: number = data?.pagination?.totalPages || 1;
  const targetYearFromApi: number | undefined = data?.targetYear;

  const effectiveYear = useMemo(
    () => targetYearFromApi ?? selectedYear,
    [targetYearFromApi, selectedYear]
  );

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const handleReadMore = (slug: string) => {
    // Adjust route to your details page
    navigate(`/ecr-blog/${slug}`);
  };

  return (
    <div className="max-w-5xl mx-auto mb-8 pt-24 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
        <div className="mb-4 sm:mb-0">
          <h2 className="text-xxxl font-bold text-foreground">Blog</h2>
          <p className="text-xs text-muted-foreground">
            {`Showing ${effectiveYear} posts${targetYearFromApi && targetYearFromApi !== selectedYear ? ` (no posts in ${selectedYear}, showing ${targetYearFromApi})` : ""}`}
          </p>
        </div>
        <div className="flex space-x-4">
          <FaList
            className={`text-2xl cursor-pointer ${viewMode === "list" ? "text-black" : "text-gray-400"}`}
            onClick={() => setViewMode("list")}
          />
          <FaThLarge
            className={`text-2xl cursor-pointer ${viewMode === "grid" ? "text-black" : "text-gray-400"}`}
            onClick={() => setViewMode("grid")}
          />
        </div>
      </div>

      {/* Loading / Error / Content */}
      {isLoading ? (
        <p>Loading blog posts...</p>
      ) : isError ? (
        <p className="text-red-500">Error loading blog posts.</p>
      ) : (
        <>
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                : "space-y-8"
            }
          >
            {posts.map((post) => {
              const cardImg = post.mainImage?.url || placeholderImg;
              const dateStr = new Date(post.date).toLocaleDateString();

              return (
                <div
                  key={post.slug}
                  className={`flex ${
                    viewMode === "list"
                      ? "flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
                      : "flex-col"
                  } border rounded-lg shadow-md`}
                >
                  <div
                    className={`${
                      viewMode === "list" ? "w-full sm:w-96 h-52" : "w-full h-72"
                    } bg-gray-300 rounded-md flex-shrink-0`}
                  >
                    <img
                      src={cardImg}
                      alt={post.title}
                      loading="lazy"
                      className="object-fill md:object-cover w-full h-full rounded-md"
                    />
                  </div>

                  <div
                    className={`flex flex-col justify-between p-4 ${
                      viewMode === "list" ? "" : "mt-4"
                    }`}
                  >
                    <h3 className="text-sm font-semibold text-foreground">
                      {post.title}
                    </h3>

                    <div className="flex items-center gap-4 text-xs text-foreground mt-1">
                      <span className="inline-flex items-center gap-2">
                        <FaCalendar className="text-primary" />
                        {dateStr}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <FaUser className="text-primary" />
                        {post.author}
                      </span>
                    </div>

                    {/* Optional excerpt if body present */}
                    {/* {post.body && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {stripHtml(post.body)}
                      </p>
                    )} */}

                    <div className="flex justify-between items-center mt-3">
                      <button
                        onClick={() => handleReadMore(post.slug)}
                        className="hover:bg-primary-600 flex-none rounded-md bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground"
                      >
                        Read More
                      </button>
                    </div>

                  <ShareBar shareLinks={post.shareLinks} absoluteUrl={post.absoluteUrl} />

                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="px-4 py-2 bg-gray-300 text-white rounded disabled:opacity-60"
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="px-4 py-2 text-primary-foreground bg-primary rounded disabled:opacity-60"
            >
              Next
            </button>
          </div>

          {/* Year Navigation */}
          <div className="mt-8">
            <h4 className="text-sm font-bold text-foreground mb-4 rounded">
              Past Years
            </h4>
            <div className="flex flex-wrap gap-4">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearChange(year)}
                  className={`border py-2 px-4 rounded-lg ${
                    selectedYear === year
                      ? "bg-primary text-primary-foreground"
                      : "border-gray-400 text-gray-700"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BlogSection;
